import { useEffect, useState } from "react";

// react-router-dom components
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

// @mui material components
// import Switch from "@mui/material/Switch";
// import Card from "@mui/material/Card";
// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { useNavigate } from "react-router-dom";
// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { useSoftUIController, loginUser } from "../../../context";
import EverIfProLogo from "assets/images/everifpro-logo.png";
import { figmaTokens } from "assets/theme/designTokens";

const APP_NAME = "eVerifPro";
import Swal from "sweetalert2";
import axios from "axios";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { getLandingRoute } from "assistant/userRole";
import { consumeAccountDeactivatedNotice, isAccountDeactivatedError } from "services/api";

function SignIn() {
  const navigate = useNavigate();
  const [controller, dispatch] = useSoftUIController();
  const [rememberMe, setRememberMe] = useState(true);
  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const { t } = useTranslate();

  useEffect(() => {
    consumeAccountDeactivatedNotice();
  }, []);

  // ✅ Yup validation schema
  const LoginSchema = Yup.object({
    email: Yup.string()
      .email(t("validation.email_invalid", "Email invalide"))
      .required(t("validation.email_required", "Email est requis")),
    password: Yup.string()
      // .min(6, "Le mot de passe doit comporter au moins 6 caractères")
      .required(t("validation.password_required", "Mot de passe est requis")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      console.log("Form submitted:", values);
      try {
        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("password", values.password);
        // Call API
        const response = await axios.post(apiurl+"Auth/login", formData);
        console.log("Login success:", response.data);
        // Save token in localStorage if available
        if (response.data.token) {
          localStorage.setItem("tk446655440000", response.data.token);
          localStorage.setItem("ps446655440000", values.password);
          loginUser(dispatch, response.data);
          navigate(getLandingRoute(response.data));
        }
        // alert("Login successful ✅");
        Swal.fire({
          icon: "success",
          title: t("auth.sign_in.success_title", "Connexion réussie"),
          showConfirmButton: false,
          timer: 700
        });
        resetForm();
      } catch (error) {
        console.error("Login failed:", error);
        if (!isAccountDeactivatedError(error)) {
          Swal.fire({
            icon: "error",
            title: t("auth.sign_in.error_title", "Adresse e-mail ou mot de passe invalide")
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <BasicLayout
      title={t("auth.sign_in.title", "Bon Retour")}
      description={t("auth.sign_in.description", "Entrez votre e-mail et votre mot de passe pour vous connecter")}
      logoComponent={
        <SoftBox display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <SoftBox
            component="img"
            src={EverIfProLogo}
            alt={APP_NAME}
            sx={{
              width: 72,
              height: 72,
              objectFit: "contain",
              display: "block",
            }}
          />
          <SoftTypography
            variant="h3"
            component="div"
            fontWeight={700}
            sx={{
              color: figmaTokens.textPrimary,
              mt: 2,
              mb: 0,
              letterSpacing: "0.06em",
              lineHeight: 1.2,
              fontSize: { xs: "1.5rem", sm: "1.65rem" },
            }}
          >
            {APP_NAME}
          </SoftTypography>
        </SoftBox>
      }
    >
        <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
          <SoftBox mb={2}>
            <SoftBox mb={1} ml={0.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                {t("auth.sign_in.email_label", "E-mail")}
            </SoftTypography>
          </SoftBox>
          <SoftInput type="email" placeholder={t("auth.sign_in.email_placeholder", "Entrez votre e-mail")} name="email" value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur} />
          {formik.touched.email && formik.errors.email ? (
            <div className="error-message">
              {formik.errors.email}
            </div>
          ) : null}
        </SoftBox>
        <SoftBox mb={2}>
          <SoftBox mb={1} ml={0.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
              {t("auth.sign_in.password_label", "Mot de passe")}
            </SoftTypography>
          </SoftBox>
          <SoftInput type="password" placeholder={t("auth.sign_in.password_placeholder", "Mot de passe")} name="password" value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur} />
            {formik.touched.password && formik.errors.password ? (
            <div className="error-message">
              {formik.errors.password}
            </div>
          ) : null}
        </SoftBox>
        {/* <SoftBox display="flex" alignItems="center">
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <SoftTypography
            variant="button"
            fontWeight="regular"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none" }}
          >
            &nbsp;&nbsp;Remember me
          </SoftTypography>
        </SoftBox> */}
        <SoftBox mt={4}>
          <SoftButton type="submit" variant="gradient" color="info" fullWidth>
            {formik.isSubmitting ? "Loading..." : t("auth.sign_in.submit", "Se Connecter")}
          </SoftButton>
        </SoftBox>
        {/* <SoftBox mt={3} textAlign="center">
          <SoftTypography variant="button" color="text" fontWeight="regular">
            Don&apos;t have an account?{" "}
            <SoftTypography
              component={Link}
              to="/authentication/sign-up"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Sign up
            </SoftTypography>
          </SoftTypography>
        </SoftBox> */}
        </SoftBox>
    </BasicLayout>
  );
}

export default SignIn;
